import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Field, Form } from 'react-final-form';
import createDecorator from 'final-form-calculate';
import { Container, Row, Col } from 'react-bootstrap';
import cx from 'classnames';
import get from 'lodash.get';
import flow from 'lodash.flow';
import { connect } from 'react-redux';
import memoize from 'memoizee';

import Step from './Step';
import Input from '../Forms/Input';
import API from '../../services/API';
import AuthService from '../../services/Auth';
import { setToken } from '../../services/Auth/actions';
import * as formValidators from '../../utils/formValidation';
import { routerPush } from '../../utils/router';
import { isValidFromString as isValidDate } from '../../utils/date';
import { countries } from '../../utils/countries';
import { withTranslation } from '../../i18n';
import Button from '../UI/Button';
import Tabs from '../UI/Tabs';
import styles from './styles.css';

const countryPrefixMap = {
  BE: '+32',
  NL: '+31',
  FR: '+33',
  DE: '+49',
  ES: '+34',
};

class StepPersonalInformation extends PureComponent {
  state = {
    isSubmitting: false,
  };

  handleSubmit = async formValues => {
    return this.props.mode === 'register' ? this.handleRegisterUser(formValues) : this.handleUpdateUser(formValues);
  };

  handleRegisterUser = async formValues => {
    this.setState({ isSubmitting: true });

    let response;
    try {
      response = await API.fetch(undefined, '/auth/register', this.getApiValuesFromFormValues(formValues));
    } catch (error) {
      // @todo handle errors properly
      this.setState({ isSubmitting: false });
      return;
    }

    this.setState({ isSubmitting: false });
    AuthService.storeToken(response.token);
    setToken(response.token);
    routerPush(`/profile?registered&crewType=${this.props.crewType}`);
  };

  handleUpdateUser = async formValues => {
    this.setState({ isSubmitting: true });

    const updatedUser = await API.fetch(undefined, '/user/update', this.getApiValuesFromFormValues(formValues));

    this.props.goToNextStep();
    this.setState({ isSubmitting: false });
    this.props.setUser(updatedUser);
  };

  getApiValuesFromFormValues = formValues => {
    const { mode } = this.props;
    const isRegistering = mode === 'register';

    return {
      facebook_user_id_token: isRegistering ? formValues.facebookUserIdToken : undefined,
      password: isRegistering ? formValues.password : undefined,
      gender: formValues.gender,
      first_name: formValues.firstName,
      last_name: formValues.lastName,
      email: formValues.email,
      address: {
        street_with_number: formValues.streetWithNumber,
        postcode: formValues.postcode,
        city: formValues.city,
        country: formValues.country,
      },
      gsm: formValues.gsm,
      birthdate: formValues.birthdate,
      language: formValues.language,
      iban: formValues.iban,
    };
  };

  getInitialValues() {
    const { user } = this.props;

    return {
      facebookUserIdToken: get(user, 'facebookUserIdToken', ''),
      gender: get(user, 'gender', 'M') || 'M',
      firstName: get(user, 'firstName', ''),
      lastName: get(user, 'lastName', ''),
      email: get(user, 'email', ''),
      streetWithNumber: get(user, 'address.streetWithNumber', ''),
      postcode: get(user, 'address.postcode', ''),
      city: get(user, 'address.city', ''),
      country: get(user, 'address.country', 'BE'),
      gsm: get(user, 'gsm', '+32'),
      birthdate: get(user, 'birthdate', ''),
      language: get(user, 'language', 'nl'),
      iban: get(user, 'iban', ''),
    };
  }

  validateForm = formValues => {
    const { mode, t } = this.props;
    const isRegistering = mode === 'register';
    const errors = {};

    formValidators.required(formValues, errors, 'gender');
    formValidators.required(formValues, errors, 'email');
    formValidators.required(formValues, errors, 'firstName');
    formValidators.required(formValues, errors, 'lastName');
    formValidators.required(formValues, errors, 'streetWithNumber');
    formValidators.required(formValues, errors, 'postcode');
    formValidators.required(formValues, errors, 'city');
    formValidators.required(formValues, errors, 'country');
    formValidators.required(formValues, errors, 'gsm');
    formValidators.required(formValues, errors, 'birthdate');
    formValidators.required(formValues, errors, 'language');

    if (!errors.birthdate && !isValidDate(formValues.birthdate)) {
      errors.birthdate = t('error.invalidDate');
    }

    if (isRegistering) {
      formValidators.required(formValues, errors, 'password');
      formValidators.required(formValues, errors, 'confirmPassword');

      if (!errors.password && !errors.confirmPassword && formValues.password !== formValues.confirmPassword) {
        errors.confirmPassword = t('error.passwordsNotEqual');
      }
    }

    return errors;
  };

  validateEmail = memoize(async value => {
    const { t } = this.props;

    if (!value || value.trim().length === 0) {
      return 'required';
    }

    try {
      const isAvailable = await API.fetch(undefined, '/auth/is-email-address-available', { email: value });
      if (!isAvailable) {
        return t('error.emailAddressAlreadyInUse');
      }
    } catch (error) {
      return;
    }
  });

  phonePrefixer = createDecorator({
    field: 'country',
    updates: {
      gsm: (country, allValues) => {
        if (!allValues.gsm || (allValues.gsm.length === 3 && typeof countryPrefixMap[country] !== 'undefined')) {
          return countryPrefixMap[country];
        }
        return allValues.gsm;
      },
    },
  });

  phoneNumberDecorator = createDecorator({
    field: 'gsm',
    updates: {
      gsm: gsm => {
        return gsm ? gsm.replace(/[^\d+]/g, '') : '';
      },
    },
  });

  render() {
    const { visible, mode, isSubmitting, t } = this.props;
    const isRegistering = mode === 'register';

    return (
      <Step visible={visible}>
        <Form
          onSubmit={this.handleSubmit}
          initialValues={this.getInitialValues()}
          validate={this.validateForm}
          decorators={[this.phonePrefixer, this.phoneNumberDecorator]}
          render={({ handleSubmit, pristine, invalid }) => (
            <form onSubmit={handleSubmit}>
              {isRegistering && <Field component="input" type="hidden" name="facebookUserIdToken" />}
              <Row>
                <Col className="mb-3" sm={12} lg={6}>
                  <Field component={Input} as="select" name="gender" label={t('gender')} required>
                    <option value="M">{t('genderM')}</option>
                    <option value="F">{t('genderF')}</option>
                    <option value="X">X</option>
                  </Field>
                </Col>
                <Col className="mb-3" sm={12} lg={6}>
                  <Field component={Input} name="firstName" label={t('firstName')} required />
                </Col>
                <Col className="mb-3" sm={12} lg={6}>
                  <Field component={Input} name="lastName" label={t('lastName')} required />
                </Col>
                <Col className="mb-3" sm={12} lg={6}>
                  <Field
                    component={Input}
                    type="email"
                    name="email"
                    label={t('emailAddress')}
                    required
                    validate={this.validateEmail}
                  />
                </Col>
              </Row>
              <Row>
                <Col className="mb-3" sm={12} lg={6}>
                  <Field component={Input} name="streetWithNumber" label={t('address')} required />
                </Col>
                <Col className="mb-3" sm={12} lg={6}>
                  <Field component={Input} name="postcode" label={t('zipCode')} required />
                </Col>
                <Col className="mb-3" sm={12} lg={6}>
                  <Field component={Input} name="city" label={t('city')} required />
                </Col>
                <Col className="mb-3" sm={12} lg={6}>
                  <Field component={Input} as="select" name="country" label={t('country')} required>
                    {countries.map(country => (
                      <option key={country.code} value={country.code}>
                        {country.label}
                      </option>
                    ))}
                  </Field>
                </Col>
              </Row>
              <Row>
                <Col className="mb-3" sm={12} lg={4}>
                  <Field component={Input} name="gsm" label={t('gsm')} helpText="(+32 477 12 34 56)" required />
                </Col>
                <Col className="mb-3" sm={12} lg={4}>
                  <Field component={Input} name="birthdate" label={t('birthdate')} helpText="(dd/mm/yyyy)" required />
                </Col>
                <Col className="mb-3" sm={12} lg={4}>
                  <Field component={Input} as="select" name="language" label={t('language')} required>
                    <option value="nl">{t('languageNL')}</option>
                    <option value="fr">{t('languageFR')}</option>
                    <option value="en">{t('languageEN')}</option>
                  </Field>
                </Col>
              </Row>
              <Row>
                <Col className="mb-3" sm={12} lg={6} className={styles.helloBank}>
                  <img src="/static/img/hellobank.svg" width="80" alt="Hello Bank" />
                  <Field
                    component={Input}
                    name="iban"
                    label={t('helloBankFieldLabel')}
                    placeholder="BE12 1234 1234 1234"
                  />
                </Col>
              </Row>

              {isRegistering && (
                <>
                  <h3 className="mt-4">{t('password')}</h3>
                  <Row>
                    <Col className="mb-3" sm={12} lg={4}>
                      <Field component={Input} name="password" label={t('password')} type="password" required />
                    </Col>
                    <Col className="mb-3" sm={12} lg={4}>
                      <Field
                        component={Input}
                        name="confirmPassword"
                        label={t('confirmPassword')}
                        type="password"
                        required
                      />
                    </Col>
                  </Row>
                </>
              )}

              <div className={cx('mt-5', styles.formButtons)}>
                <Button type="submit" processing={isSubmitting}>
                  {t('next')}
                </Button>
              </div>
            </form>
          )}
        />
      </Step>
    );
  }
}

StepPersonalInformation.propTypes = {
  visible: PropTypes.bool,
  user: PropTypes.object.isRequired,
  mode: PropTypes.oneOf(['register', 'update']),
};

StepPersonalInformation.defaultProps = {
  mode: 'update',
};

const mapDispatchToProps = {
  setToken,
};

export default flow(
  connect(
    null,
    mapDispatchToProps,
  ),
  withTranslation('common'),
)(StepPersonalInformation);
